import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { memo, useId, useState } from 'react';
import { useFieldArray, type Control } from 'react-hook-form';

import { Button } from '@/components/ui/Button';

import { DocumentPickerDialog } from './DocumentPickerDialog';
import type { PageForm } from './pageLabels';

interface LinkedContentFieldProps {
  control: Control<PageForm>;
  /** From the reply's `limits` (P3, P31). */
  maxLinkedDocuments: number;
  /** The form's refusal of the links, or a save's (B4). */
  error?: string;
  disabled?: boolean;
}

/** A row that cannot be used yet, its reason in words rather than only greyed out. */
function LaterRow({
  label,
  reason,
  testId,
}: {
  label: string;
  reason: string;
  testId: string;
}) {
  return (
    <Paper
      variant="outlined"
      aria-disabled="true"
      data-testid={testId}
      sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', gap: 2 }}
    >
      <Typography color="text.secondary">{label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {reason}
      </Typography>
    </Paper>
  );
}

/**
 * Linked Content (PB8, P14, P15, P39): documents now, in the order added;
 * campaigns and petitions arrive with their modules (backend D14), so their
 * rows say so. A linked document that is no longer published stays, marked
 * "Unavailable", for the author to remove (backend D15).
 */
export const LinkedContentField = memo(function LinkedContentField({
  control,
  maxLinkedDocuments,
  error,
  disabled = false,
}: LinkedContentFieldProps) {
  const errorId = useId();
  const [picking, setPicking] = useState(false);
  const { fields, append, remove } = useFieldArray({ control, name: 'links' });
  const documents = fields.filter((link) => link.resourceType === 'DOCUMENT');
  const atLimit = documents.length >= maxLinkedDocuments;

  return (
    <Box
      role="group"
      aria-label="Linked Content"
      aria-describedby={error ? errorId : undefined}
      data-testid="page-editor-linked-content"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <Typography variant="h6" component="h2">
        Linked Content
      </Typography>

      <LaterRow
        label="Campaign"
        reason="Arrives with Campaigns"
        testId="page-editor-linked-campaign-row"
      />
      <LaterRow
        label="Petition"
        reason="Arrives with Petitions"
        testId="page-editor-linked-petition-row"
      />

      <Typography variant="subtitle2" component="h3">
        Documents
      </Typography>
      {documents.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="page-editor-linked-documents-empty-label"
        >
          No documents linked
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {fields.map((link, index) =>
            link.resourceType === 'DOCUMENT' ? (
              <Box
                component="li"
                key={link.id}
                data-testid={`page-editor-linked-document-${link.resourceId}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ overflowWrap: 'anywhere' }}>
                    {link.title ?? link.resourceId}
                  </Typography>
                  {link.available ? null : (
                    <Typography
                      variant="body2"
                      color="warning.main"
                      data-testid={`page-editor-linked-document-${link.resourceId}-unavailable-label`}
                    >
                      Unavailable
                    </Typography>
                  )}
                </Box>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={disabled}
                  aria-label={`Remove ${link.title ?? link.resourceId}`}
                  onClick={() => remove(index)}
                  data-testid={`page-editor-linked-document-${link.resourceId}-remove-button`}
                >
                  Remove
                </Button>
              </Box>
            ) : null,
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || atLimit}
          onClick={() => setPicking(true)}
          data-testid="page-editor-add-document-button"
        >
          Add document
        </Button>
        {atLimit ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="page-editor-linked-documents-limit-value"
          >
            Up to {maxLinkedDocuments} documents
          </Typography>
        ) : null}
      </Box>

      {error ? (
        <FormHelperText
          id={errorId}
          error
          role="alert"
          data-testid="page-editor-linked-content-error"
        >
          {error}
        </FormHelperText>
      ) : null}

      {picking ? (
        <DocumentPickerDialog
          linkedIds={documents.map((link) => link.resourceId)}
          maxLinkedDocuments={maxLinkedDocuments}
          onPick={(link) => append(link)}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </Box>
  );
});
